import { useCallback, useEffect, useState } from "react";
import { Input } from "antd";
import { maskMoney } from "../../../utils/mask";
import { usePropostaForm } from "../context/PropostaFormContext";
import { usePropostaPagamentos } from "../hooks/usePropostaPagamentos";
import { usePropostaToast } from "../hooks/usePropostaToast";
import { IPagamento } from "../types/proposta";
import { moneyToNumber } from "../utils/propostaMappers";
import { formatPagamentoParcelas } from "../utils/formatPagamentoParcelas";
import PropostaSectionCard from "../components/PropostaSectionCard";
import PropostaSelect from "../components/PropostaSelect";

export default function PropostaPagamentosSection() {
  const { proposta, setProposta, isReadOnly, isDark } = usePropostaForm();
  const { condPagOptions } = usePropostaPagamentos();
  const toast = usePropostaToast();

  const [idFormaPag, setIdFormaPag] = useState("");
  const [descricaoFormaPag, setDescricaoFormaPag] = useState("");
  const [valorPagamento, setValorPagamento] = useState("");
  const [qtdParcelas, setQtdParcelas] = useState("");
  const [firstDueDate, setFirstDueDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [detalhamento, setDetalhamento] = useState("");

  const pagOptions = condPagOptions.map((item) => ({
    label: item.description,
    value: String(item.id),
  }));

  const getSaldo = useCallback(() => {
    let adicionado = 0;
    proposta.pagamentos?.forEach((p) => {
      adicionado += moneyToNumber(p.valor);
    });
    const saldo = moneyToNumber(proposta.imovel?.valor) - adicionado;
    if (saldo > 0) {
      setValorPagamento(
        saldo.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
      );
    }
    return saldo;
  }, [proposta.pagamentos, proposta.imovel?.valor]);

  useEffect(() => {
    if (idFormaPag) getSaldo();
  }, [idFormaPag, getSaldo]);

  const addPagamento = () => {
    if (!idFormaPag) {
      toast.info("Selecione uma forma de pagamento!");
      return;
    }
    if (!moneyToNumber(valorPagamento)) {
      toast.info("Selecione o valor do pagamento!");
      return;
    }
    if (!qtdParcelas) {
      toast.info("Selecione a quantidade de parcelas!");
      return;
    }
    if (!firstDueDate) {
      toast.info("Selecione a data do primeiro vencimento!");
      return;
    }

    let valorPago = 0;
    proposta.pagamentos?.forEach((p) => {
      valorPago += moneyToNumber(p.valor);
    });

    const valorPagamentoNum = moneyToNumber(valorPagamento);
    const propostaValor = moneyToNumber(proposta.imovel?.valor);

    if (Number((valorPago + valorPagamentoNum).toFixed(2)) > propostaValor) {
      toast.info("O valor informado é superior ao valor da proposta!");
      return;
    }

    const tempPagamento: IPagamento = {
      id_formapagcondpag: idFormaPag,
      qtd_parcelas: qtdParcelas,
      valor: maskMoney(valorPagamento),
      descricao: descricaoFormaPag,
      detalhamento,
      dt_vencimento: firstDueDate,
    };

    setProposta((prev) => ({
      ...prev,
      pagamentos: [...(prev.pagamentos || []), tempPagamento],
    }));

    setDetalhamento("");
    setDescricaoFormaPag("");
    setQtdParcelas("");
    setValorPagamento("");
    setIdFormaPag("");
  };

  const removePagamento = (index: number) => {
    const pagamentos = [...(proposta.pagamentos || [])];
    pagamentos.splice(index, 1);
    setProposta((prev) => ({ ...prev, pagamentos }));
  };

  const valorTotal = moneyToNumber(proposta.imovel?.valor);
  const valorPago = (proposta.pagamentos || []).reduce(
    (acc, p) => acc + moneyToNumber(p.valor),
    0,
  );
  const saldo = valorTotal - valorPago;

  return (
    <div className="space-y-4">
      <div
        className={`flex flex-wrap gap-4 text-sm p-3 rounded-lg ${
          isDark ? "bg-[#24304a] text-gray-100" : "bg-[#f8f9fb] text-gray-900"
        }`}
      >
        <span>
          Valor da Proposta:{" "}
          <strong>
            {valorTotal.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}
          </strong>
        </span>
        <span>
          Valor Adicionado:{" "}
          <strong>
            {valorPago.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}
          </strong>
        </span>
        <span>
          Saldo:{" "}
          <strong className={saldo < 0 ? "text-red-500" : "text-primary"}>
            {saldo.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}
          </strong>
        </span>
      </div>

      {!isReadOnly && (
        <PropostaSectionCard title="Adicionar Pagamento">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Forma de Pagamento *">
              <PropostaSelect
                className="w-full"
                placeholder="Selecione"
                value={idFormaPag || undefined}
                onChange={(value) => {
                  setIdFormaPag(value);
                  const opt = pagOptions.find((o) => o.value === value);
                  if (opt) setDescricaoFormaPag(opt.label);
                }}
                options={pagOptions}
              />
            </Field>
            <Field label="Valor do Pagamento *">
              <Input
                value={valorPagamento}
                onChange={(e) => setValorPagamento(maskMoney(e.target.value))}
              />
            </Field>
            <Field label="Parcelas *">
              <Input
                type="number"
                min={1}
                value={qtdParcelas}
                onChange={(e) => setQtdParcelas(e.target.value)}
              />
            </Field>

            <Field label="1º Vencimento *">
              <Input
                type="date"
                value={firstDueDate}
                onChange={(e) => setFirstDueDate(e.target.value)}
              />
            </Field>
            <Field label="Detalhamento">
              <Input
                value={detalhamento}
                onChange={(e) => setDetalhamento(e.target.value)}
              />
            </Field>
            <div className="flex items-end md:col-span-2">
              <button
                type="button"
                onClick={addPagamento}
                className="px-4 py-2 rounded-md bg-primary text-white font-semibold text-sm"
              >
                + Adicionar Pagamento
              </button>
            </div>
          </div>
        </PropostaSectionCard>
      )}

      {proposta.pagamentos?.length ? (
        proposta.pagamentos.map((p, index) => (
          <div
            key={index}
            className={`p-4 rounded-2xl border flex justify-between ${
              isDark
                ? "bg-[#1d2840] border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div>
              <p className="font-semibold">{p.descricao || p.forma}</p>
              <p
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {formatPagamentoParcelas(p)}
              </p>
              {p.detalhamento?.trim() ? (
                <p
                  className={`italic text-sm mt-0.5 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {p.detalhamento}
                </p>
              ) : null}
              {p.dt_vencimento && (
                <p
                  className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  Vencimento: {p.dt_vencimento}
                </p>
              )}
            </div>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => removePagamento(index)}
                className="w-fit h-fit p-0 text-red-500 text-sm hover:underline"
              >
                Remover
              </button>
            )}
          </div>
        ))
      ) : (
        <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          Nenhum pagamento adicionado.
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const { isDark } = usePropostaForm();

  return (
    <div>
      <label
        className={`block text-sm font-medium mb-1 ${
          isDark ? "text-gray-300" : "text-gray-700"
        }`}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
